import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Hero } from '../components/Hero';
import { CategoryCard } from '../components/CategoryCard';
import { ProductCard } from '../components/ProductCard';
import { PersonalizationSection } from '../components/PersonalizationSection';
import { Footer } from '../components/Footer';
import { CustomizationWizard } from '../components/occasions/CustomizationWizard';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { getPersonalizedGreeting } from '../utils/greeting';
import { Sparkles, ShoppingBag } from 'lucide-react';
export function Home() {
  const {
    products,
    categories
  } = useData();
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  // Get featured products (e.g., first 6 or specific logic)
  const featuredProducts = products.slice(0, 6);
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-2639">
      <Navbar data-id="element-2640" />

      <main data-id="element-2641">
        {/* Personalized Greeting Banner for Logged-in Users */}
        {isAuthenticated && user && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-gradient-to-r from-rose-50 to-purple-50 border-b border-rose-100 mt-20" data-id="element-2642">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-2643">
              <div className="flex items-center justify-between" data-id="element-2644">
                <div className="flex items-center space-x-3" data-id="element-2645">
                  <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-2646">
                    <Sparkles className="w-6 h-6 text-rose-500" data-id="element-2647" />
                  </div>
                  <div data-id="element-2648">
                    <h2 className="text-2xl font-serif font-bold text-gray-900" data-id="element-2649">
                      {getPersonalizedGreeting(user.name, user.surname)}!
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-2650">
                      Welcome back to Bloom & Pop
                    </p>
                  </div>
                </div>
                <Link to="/dashboard" data-id="element-2651">
                  <button className="hidden sm:block px-6 py-2 bg-rose-500 text-white rounded-full font-medium hover:bg-rose-600 transition-colors" data-id="element-2652">
                    View Dashboard
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>}

        <Hero data-id="element-2653" />

        {/* Categories Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2654">
          <div className="text-center mb-16" data-id="element-2655">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-2656">
              Shop by Category
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2657">
              Find the perfect gift for any occasion from our main collections.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2658">
            {categories.map((category, index) => {
            // Map category titles to their respective page routes
            let href = '#';
            if (category.title.toLowerCase().includes('balloon')) {
              href = '/balloons';
            } else if (category.title.toLowerCase().includes('gift')) {
              href = '/gift-boxes';
            } else if (category.title.toLowerCase().includes('flower')) {
              href = '/flowers';
            }
            return <CategoryCard key={category.id} title={category.title} description={category.description} image={category.image} color={category.color} delay={index * 0.2} href={href} data-id="element-2659" />;
          })}
          </div>
        </section>

        {/* Featured Products */}
        <section className="py-20 bg-gray-50" data-id="element-2660">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2661">
            <div className="flex justify-between items-end mb-12" data-id="element-2662">
              <div data-id="element-2663">
                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-2664">
                  Trending Now
                </h2>
                <p className="text-gray-600" data-id="element-2665">Our most loved gifts this week.</p>
              </div>
              <Link to="/shop" className="hidden md:block text-rose-500 font-medium hover:text-rose-600 transition-colors" data-id="element-2666">
                View all products &rarr;
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2667">
              {featuredProducts.map(product => <ProductCard key={product.id} product={product} name={product.name} price={product.price} image={product.image} category={product.category} isNew={product.isNew} data-id="element-2668" />)}
            </div>

            <div className="mt-12 text-center md:hidden" data-id="element-2669">
              <Link to="/shop" className="text-rose-500 font-medium hover:text-rose-600 transition-colors" data-id="element-2670">
                View all products &rarr;
              </Link>
            </div>
          </div>
        </section>

        <PersonalizationSection data-id="element-2671" />

        {/* Newsletter / CTA Section */}
        <section className="py-24 bg-rose-900 text-white relative overflow-hidden" data-id="element-2672">
          <div className="absolute inset-0 opacity-10" data-id="element-2673">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-2674"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-2675">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6" data-id="element-2676">
              Let's Create Something Beautiful
            </h2>
            <p className="text-rose-100 text-lg mb-10 max-w-2xl mx-auto" data-id="element-2677">
              Join our newsletter for exclusive offers, gift inspiration, and
              early access to new collections.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto" data-id="element-2678">
              <input type="email" placeholder="Enter your email" className="px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 w-full" data-id="element-2679" />
              <button className="px-8 py-4 bg-white text-rose-900 rounded-full font-bold hover:bg-rose-50 transition-colors whitespace-nowrap" data-id="element-2680">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2681" />

      {/* Customization Wizard Modal */}
      <CustomizationWizard isOpen={isCustomizationOpen} onClose={() => setIsCustomizationOpen(false)} data-id="element-2682" />
    </div>;
}